import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { Add, Menu, Send } from '@mui/icons-material';
import NereusCaution from './NereusCaution';
import AppNavigation from './AppNavigation';
import ChatBubble from './ChatBubble';
import NereusAvatar from './NereusAvatar';
import { useChat } from '../hooks/useChat';

const SIDEBAR_WIDTH = 280;

const SUGGESTED_PROMPTS = [
  'How can I improve my air consumption?',
  'Plan a nitrox fill for a 60 ft dive',
  'What should I check before a cold-water dive?',
  'Which NAUI cert should I work toward next?',
  'What did I miss in my group chats?',
  'Anything coming up with my groups and clubs?',
];

const ChatPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const autoSentRef = useRef(false);

  const {
    conversations,
    messages,
    currentConversationId,
    loading,
    sending,
    error,
    loadConversations,
    loadHistory,
    sendMessage,
    startNewConversation,
  } = useChat();

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-send a message handed off via router state (e.g. "Ask Nereus about this dive").
  // The state is cleared immediately so a page refresh doesn't re-send it.
  useEffect(() => {
    const initialMessage = (location.state as { initialMessage?: string } | null)?.initialMessage;
    if (initialMessage && !autoSentRef.current) {
      autoSentRef.current = true;
      startNewConversation();
      navigate(location.pathname, { replace: true });
      sendMessage(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;
    setInputText('');
    await sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const sidebar = (
    <Box sx={{ width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={600}>Conversations</Typography>
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={startNewConversation}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <List dense sx={{ flex: 1, overflowY: 'auto' }}>
        {conversations.map(c => (
          <ListItemButton
            key={c.conversationId}
            selected={c.conversationId === currentConversationId}
            onClick={() => {
              loadHistory(c.conversationId);
              if (isMobile) setSidebarOpen(false);
            }}
          >
            <ListItemText
              primary={c.title}
              primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }}
            />
          </ListItemButton>
        ))}
        {conversations.length === 0 && !loading && (
          <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
            No conversations yet
          </Typography>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppNavigation />
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <Box sx={{ width: SIDEBAR_WIDTH, borderRight: 1, borderColor: 'divider', flexShrink: 0 }}>
            {sidebar}
          </Box>
        )}

        {/* Mobile drawer */}
        {isMobile && (
          <Drawer open={sidebarOpen} onClose={() => setSidebarOpen(false)} PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
            {sidebar}
          </Drawer>
        )}

        {/* Main chat area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
            {isMobile && (
              <IconButton size="small" onClick={() => setSidebarOpen(true)}>
                <Menu />
              </IconButton>
            )}
            <NereusAvatar size={30} />
            <Typography variant="subtitle1" fontWeight={600}>Nereus — New Achievement Scuba</Typography>
          </Box>

          <NereusCaution />

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
            {messages.length === 0 && !loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary' }}>
                <NereusAvatar size={72} />
                <Typography variant="body2" textAlign="center">
                  I'm Nereus, keeper of the deep. Let's chat about diving!
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 520 }}>
                  {SUGGESTED_PROMPTS.map(prompt => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      variant="outlined"
                      color="primary"
                      onClick={() => sendMessage(prompt)}
                      disabled={sending}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {messages.map(m => (
              <ChatBubble key={m.messageId} message={m} />
            ))}
            {sending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Ask Nereus a question…"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputText.trim() || sending}
              sx={{ mb: 0.25 }}
            >
              <Send />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatPage;
